/**
 * Réglages IA d’une galerie : lecture sous console:read, écriture motivée sous
 * console:manage-addons hors galerie fermée. Dollars à l’écran, cents entiers
 * vers l’API et null pour le défaut ; seul le serveur confirme les réglages.
 */
import { useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Checkbox, Input, Textarea } from '@artosera/ui';
import { iaConsoleApi, type GalerieIa, type ReglagesIa } from '../../lib/ia.js';
import { usePeut } from '../../auth/usePeut.js';
import page from '../../styles/page.module.css';
import styles from '../Ia.module.css';

export function IaGalerie({
  idGalerie,
  fermee,
}: {
  idGalerie: string;
  fermee: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation('console');
  const client = useQueryClient();
  const peut = usePeut();
  const cle = ['console', 'galerie', idGalerie, 'ia'];
  const requete = useQuery({
    queryKey: cle,
    queryFn: () => iaConsoleApi.galerie(idGalerie),
    enabled: peut('console:read'),
  });
  const [succes, setSucces] = useState(false);
  const mutation = useMutation({
    mutationFn: (corps: ReglagesIa) => iaConsoleApi.regler(idGalerie, corps),
    onMutate: () => setSucces(false),
    onSuccess: (data) => {
      client.setQueryData(cle, data);
      setSucces(true);
    },
  });
  const dollars = (cents: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  const data = requete.data;
  const moisParis = new Intl.DateTimeFormat('fr-CA', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
  }).format(new Date());
  const [annee, mois] = moisParis.split('-').map(Number);
  const mois12 = Array.from({ length: 12 }, (_, i) =>
    new Date(Date.UTC(annee!, mois! - 1 - i, 1)).toISOString().slice(0, 7),
  );
  return (
    <section className={`${page.panneau} ${styles.bloc}`} aria-labelledby="galerie-ia">
      <h2 id="galerie-ia">{t('ia.titre')}</h2>
      {requete.isPending && <p>{t('ia.chargement')}</p>}
      {requete.isError && <p role="alert">{t('ia.erreur')}</p>}
      {data && (
        <>
          <p role="status">{t(data.suspendue ? 'ia.suspensionOui' : 'ia.suspensionNon')}</p>
          <p>{t('ia.effectif', { montant: dollars(data.plafondEffectifCents) })}</p>
          {data.motif && <p>{data.motif}</p>}
          <Formulaire
            key={`${idGalerie}:${data.plafondMensuelCents}:${data.suspendue}:${data.motif}`}
            data={data}
            modifiable={peut('console:manage-addons') && !fermee}
            enCours={mutation.isPending}
            enregistrer={(corps) => mutation.mutate(corps)}
          />
          {mutation.isError && <p role="alert">{t('ia.erreur')}</p>}
          {succes && <p role="status">{t('ia.enregistre')}</p>}
          <h3>{t('ia.consommations')}</h3>
          <ul className={styles.mois}>
            {mois12.map((m) => {
              const c = data.consommations.find((c) => c.mois === m);
              return (
                <li key={m}>
                  <details>
                    <summary>
                      {m} · {dollars(c?.depenseCents ?? 0)} ·{' '}
                      {t('ia.appels', { n: c?.appels ?? 0 })}
                    </summary>
                    <h4>{t('ia.parUsage')}</h4>
                    {c && Object.keys(c.parUsage).length ? (
                      <ul>
                        {Object.entries(c.parUsage).map(([u, v]) => (
                          <li key={u}>
                            {t(`ia.usages.${u}`, { defaultValue: u, nsSeparator: false })} ·{' '}
                            {dollars(v.depenseCents)} · {t('ia.appels', { n: v.appels })}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p>{t('ia.aucune')}</p>
                    )}
                  </details>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}

function Formulaire({
  data,
  modifiable,
  enCours,
  enregistrer,
}: {
  data: GalerieIa;
  modifiable: boolean;
  enCours: boolean;
  enregistrer: (c: ReglagesIa) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const [plafond, setPlafond] = useState(
    data.plafondMensuelCents === null ? '' : (data.plafondMensuelCents / 100).toFixed(2),
  );
  const [suspendue, setSuspendue] = useState(data.suspendue);
  const [motif, setMotif] = useState('');
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        if (modifiable && motif.trim() && !enCours)
          enregistrer({
            plafondMensuelCents: plafond === '' ? null : Math.round(Number(plafond) * 100),
            suspendue,
            motif,
          });
      }}
    >
      {!modifiable && <p>{t('ia.lectureSeule')}</p>}
      <fieldset disabled={!modifiable || enCours} className={styles.formulaire}>
        <legend>{t('ia.titre')}</legend>
        <label htmlFor="ia-plafond">{t('ia.plafond')}</label>
        <Input
          id="ia-plafond"
          type="number"
          min={0}
          max={1000}
          step="0.01"
          value={plafond}
          onChange={(e) => setPlafond(e.target.value)}
          aria-describedby="ia-plafond-aide"
        />
        <p id="ia-plafond-aide">{t('ia.plafondAide')}</p>
        <Checkbox
          id="ia-suspendue"
          label={t('ia.suspendue')}
          checked={suspendue}
          onCheckedChange={(v) => setSuspendue(v === true)}
        />
        {modifiable && (
          <>
            <label htmlFor="ia-galerie-motif">{t('ia.motif')}</label>
            <Textarea
              id="ia-galerie-motif"
              required
              maxLength={500}
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
            <Button type="submit" disabled={!motif.trim()}>
              {t('ia.enregistrer')}
            </Button>
          </>
        )}
      </fieldset>
    </form>
  );
}
